import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  CalendarMonth as CalendarMonthIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory2 as Inventory2Icon,
  LocalShipping as LocalShippingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import StockTransferService from "../../services/StockTransferService";

const PAGE_SIZE = 50;

/**
 * ============================================================
 * UNIFIED DISPATCH / RETURN OUTBOUND PAGE
 * ============================================================
 *
 * Supported authenticated users:
 *
 * factorymanager    -> factory
 * devicemanufacture -> manufacturer
 * dealer            -> dealer
 *
 * IMPORTANT:
 * ------------------------------------------------------------
 * We DO NOT send:
 *
 * role
 * user_type
 * party_type
 * user_id
 *
 * Backend identifies the authenticated caller automatically.
 *
 * Outbound endpoint returns registers where the authenticated
 * caller is the SENDER.
 *
 * Example:
 *
 * factory -> manufacturer
 * manufacturer -> dealer
 * manufacturer -> factory
 * dealer -> manufacturer
 *
 * ============================================================
 */

const DispatchOutbound = () => {
  /**
   * ==========================================================
   * STATE
   * ==========================================================
   */

  const [registers, setRegisters] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [expandedRegister, setExpandedRegister] = useState(null);

  /**
   * ==========================================================
   * FILTER STATE
   * ==========================================================
   */

  const [imei, setImei] = useState("");

  const [direction, setDirection] = useState("");

  const [receiverType, setReceiverType] = useState("");

  const [receiverId, setReceiverId] = useState("");

  const [dateFrom, setDateFrom] = useState("");

  const [dateTo, setDateTo] = useState("");

  /**
   * Applied filters.
   *
   * Keeping these separate from input fields prevents the API
   * from being called on every keystroke.
   */
  const [appliedFilters, setAppliedFilters] = useState({});

  /**
   * ==========================================================
   * DETAIL DIALOG
   * ==========================================================
   */

  const [detailDialog, setDetailDialog] = useState({
    open: false,
    register: null,
  });

  const [detailLoading, setDetailLoading] = useState(false);

  /**
   * ==========================================================
   * SNACKBAR
   * ==========================================================
   */

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  /**
   * ==========================================================
   * SHOW SNACKBAR
   * ==========================================================
   */

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  /**
   * ==========================================================
   * CLOSE SNACKBAR
   * ==========================================================
   */

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /**
   * ==========================================================
   * ERROR MESSAGE
   * ==========================================================
   */

  const getErrorMessage = (error) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong"
    );
  };

  /**
   * ==========================================================
   * BUILD FILTER PAYLOAD
   * ==========================================================
   *
   * Only non-empty filters are sent.
   *
   * We intentionally DO NOT add:
   *
   * role
   * user_id
   * sender_id
   * sender_type
   *
   * because outbound is automatically scoped to the
   * authenticated sender by the backend.
   */

  const buildFilterPayload = () => {
    const payload = {};

    if (imei.trim()) {
      payload.imei = imei.trim();
    }

    if (direction) {
      payload.direction = direction;
    }

    /**
     * Receiver filters are optional.
     *
     * receiver_type and receiver_id must be sent together
     * according to the API specification.
     */
    if (receiverType && receiverId) {
      payload.receiver_type = receiverType;
      payload.receiver_id = Number(receiverId);
    }

    if (dateFrom) {
      payload.date_from = dateFrom;
    }

    if (dateTo) {
      payload.date_to = dateTo;
    }

    return payload;
  };

  /**
   * ==========================================================
   * LOAD OUTBOUND REGISTERS
   * ==========================================================
   *
   * POST
   *
   * /api/stock/transfer/outbound/
   *
   * Backend automatically identifies the sender from
   * authentication.
   */

  const loadOutboundRegisters = async (
    customFilters = appliedFilters
  ) => {
    setLoading(true);

    try {
      const payload = {
        ...customFilters,
        page,
        page_size: PAGE_SIZE,
      };

      const response =
        await StockTransferService.getOutboundTransfers(
          payload
        );

      const data = response?.data || {};

      const results = Array.isArray(data.results)
        ? data.results
        : [];

      setRegisters(results);

      setTotal(
        Number(data.total || 0)
      );

      /**
       * Collapse the previously opened register after
       * a new search/page load.
       */
      setExpandedRegister(null);

    } catch (error) {
      console.error(
        "Outbound register error:",
        error
      );

      showSnackbar(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * ==========================================================
   * INITIAL LOAD
   * ==========================================================
   */

  useEffect(() => {
    loadOutboundRegisters();
  }, [page, appliedFilters]);

  /**
   * ==========================================================
   * APPLY FILTERS
   * ==========================================================
   */

  const handleApplyFilters = () => {
    /**
     * Receiver ID without receiver type is invalid according
     * to the API contract.
     */
    if (receiverId && !receiverType) {
      showSnackbar(
        "Please select receiver type before entering receiver ID.",
        "warning"
      );

      return;
    }

    if (receiverType && !receiverId) {
      showSnackbar(
        "Please enter receiver ID.",
        "warning"
      );

      return;
    }

    /**
     * Date validation.
     */
    if (
      dateFrom &&
      dateTo &&
      dateFrom > dateTo
    ) {
      showSnackbar(
        "Date From cannot be greater than Date To.",
        "warning"
      );

      return;
    }

    const filters = buildFilterPayload();

    setPage(1);

    setAppliedFilters(filters);
  };

  /**
   * ==========================================================
   * CLEAR FILTERS
   * ==========================================================
   */

  const handleClearFilters = () => {
    setImei("");
    setDirection("");
    setReceiverType("");
    setReceiverId("");
    setDateFrom("");
    setDateTo("");

    setPage(1);
    setAppliedFilters({});
  };

  /**
   * ==========================================================
   * REFRESH
   * ==========================================================
   */

  const handleRefresh = () => {
    loadOutboundRegisters();
  };

  /**
   * ==========================================================
   * SUMMARY COUNTS
   * ==========================================================
   */

  const totalItems = useMemo(() => {
    return registers.reduce(
      (count, register) =>
        count +
        Number(
          register.item_count || 0
        ),
      0
    );
  }, [registers]);

  const totalPendingItems = useMemo(() => {
    return registers.reduce(
      (count, register) =>
        count +
        Number(
          register.pending_count || 0
        ),
      0
    );
  }, [registers]);

  const totalAcceptedItems = useMemo(() => {
    return registers.reduce(
      (count, register) =>
        count +
        Number(
          register.accepted_count || 0
        ),
      0
    );
  }, [registers]);

  const totalRejectedItems = useMemo(() => {
    return registers.reduce(
      (count, register) =>
        count +
        Number(
          register.rejected_count || 0
        ),
      0
    );
  }, [registers]);

  /**
   * ==========================================================
   * FORMAT DATE
   * ==========================================================
   */

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    try {
      return new Date(date).toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    } catch {
      return date;
    }
  };

  /**
   * ==========================================================
   * FORMAT DATE + TIME
   * ==========================================================
   */

  const formatDateTime = (date) => {
    if (!date) {
      return "-";
    }

    try {
      return new Date(date).toLocaleString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    } catch {
      return date;
    }
  };

  /**
   * ==========================================================
   * DIRECTION LABEL
   * ==========================================================
   */

  const getDirectionLabel = (directionValue) => {
    const labels = {
      factory_to_manufacturer:
        "Factory → Manufacturer",

      manufacturer_to_dealer:
        "Manufacturer → Dealer",

      dealer_to_manufacturer:
        "Dealer → Manufacturer",

      manufacturer_to_factory:
        "Manufacturer → Factory",
    };

    return (
      labels[directionValue] ||
      directionValue ||
      "Transfer"
    );
  };

  /**
   * ==========================================================
   * DIRECTION COLOR
   * ==========================================================
   */

  const getDirectionColor = (directionValue) => {
    switch (directionValue) {
      case "factory_to_manufacturer":
        return "info";

      case "manufacturer_to_dealer":
        return "primary";

      case "dealer_to_manufacturer":
        return "warning";

      case "manufacturer_to_factory":
        return "secondary";

      default:
        return "default";
    }
  };

  /**
   * ==========================================================
   * STATUS CHIP
   * ==========================================================
   */

  const renderStatusChip = (status) => {
    if (status === "pending") {
      return (
        <Chip
          label="In Transit"
          size="small"
          color="warning"
          variant="outlined"
          icon={<ScheduleIcon />}
        />
      );
    }

    if (status === "accepted") {
      return (
        <Chip
          label="Received"
          size="small"
          color="success"
          icon={<CheckCircleIcon />}
        />
      );
    }

    // if (status === "rejected") {
    //   return (
    //     <Chip
    //       label="Rejected"
    //       size="small"
    //       color="error"
    //       icon={<CancelIcon />}
    //     />
    //   );
    // }

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        variant="outlined"
      />
    );
  };

  /**
   * ==========================================================
   * TOGGLE REGISTER
   * ==========================================================
   */

  const toggleRegister = (registerId) => {
    setExpandedRegister(
      (current) =>
        current === registerId
          ? null
          : registerId
    );
  };

  /**
   * ==========================================================
   * OPEN DETAIL
   * ==========================================================
   *
   * Uses:
   *
   * GET /api/stock/transfer/<id>/
   *
   * This gives the complete register detail.
   */

  const handleOpenDetail = async (
    registerId
  ) => {
    if (!registerId) {
      return;
    }

    setDetailLoading(true);

    try {
      const response =
        await StockTransferService.getTransferDetail(
          registerId
        );

      const data =
        response?.data || {};

      setDetailDialog({
        open: true,
        register: data,
      });
    } catch (error) {
      console.error(
        "Transfer detail error:",
        error
      );

      showSnackbar(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * ==========================================================
   * CLOSE DETAIL
   * ==========================================================
   */

  const handleCloseDetail = () => {
    setDetailDialog({
      open: false,
      register: null,
    });
  };

  /**
   * ==========================================================
   * RENDER
   * ==========================================================
   */

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        background:
          "#f8fafc",
        minHeight:
          "100vh",
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border:
            "1px solid #e2e8f0",
          boxShadow:
            "0 4px 20px rgba(15,23,42,0.05)",
        }}
      >
        <CardContent>
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "flex-start",
              md: "center",
            }}
            spacing={2}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg,#2563eb,#7c3aed)",
                    color: "#fff",
                  }}
                >
                  <LocalShippingIcon />
                </Box>

                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color="#0f172a"
                  >
                    Dispatch Outbound
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Track devices dispatched by
                    you and monitor acceptance,
                    rejection, and in-transit  status.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Button
              variant="outlined"
              startIcon={
                loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={handleRefresh}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Refresh
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ======================================================
          FILTERS
      ====================================================== */}

      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border:
            "1px solid #e2e8f0",
          boxShadow:
            "0 3px 15px rgba(15,23,42,0.04)",
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <SearchIcon
              color="primary"
            />

            <Typography
              variant="h6"
              fontWeight={800}
            >
              Search & Filters
            </Typography>
          </Stack>

          <Grid
            container
            spacing={2}
          >
            {/* IMEI */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                fullWidth
                size="small"
                label="IMEI"
                placeholder="Search IMEI"
                value={imei}
                onChange={(e) =>
                  setImei(e.target.value)
                }
              />
            </Grid>

            {/* DIRECTION */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Direction"
                value={direction}
                onChange={(e) =>
                  setDirection(
                    e.target.value
                  )
                }
              >
                <MenuItem value="">
                  All Directions
                </MenuItem>

                <MenuItem value="factory_to_manufacturer">
                  Factory → Manufacturer
                </MenuItem>

                <MenuItem value="manufacturer_to_dealer">
                  Manufacturer → Dealer
                </MenuItem>

                <MenuItem value="dealer_to_manufacturer">
                  Dealer → Manufacturer
                </MenuItem>

                <MenuItem value="manufacturer_to_factory">
                  Manufacturer → Factory
                </MenuItem>
              </TextField>
            </Grid>

            {/* RECEIVER TYPE */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Receiver Type"
                value={receiverType}
                onChange={(e) =>
                  setReceiverType(
                    e.target.value
                  )
                }
              >
                <MenuItem value="">
                  All Receivers
                </MenuItem>

                <MenuItem value="dealer">
                  Dealer
                </MenuItem>

                <MenuItem value="manufacturer">
                  Manufacturer
                </MenuItem>

                <MenuItem value="factory">
                  Factory
                </MenuItem>
              </TextField>
            </Grid>

            {/* RECEIVER ID */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                fullWidth
                size="small"
                label="Receiver ID"
                type="number"
                placeholder="Enter receiver ID"
                value={receiverId}
                onChange={(e) =>
                  setReceiverId(
                    e.target.value
                  )
                }
                disabled={!receiverType}
              />
            </Grid>

            {/* DATE FROM */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date From"
                value={dateFrom}
                onChange={(e) =>
                  setDateFrom(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <CalendarMonthIcon
                      sx={{
                        mr: 1,
                        color:
                          "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            {/* DATE TO */}

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date To"
                value={dateTo}
                onChange={(e) =>
                  setDateTo(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <CalendarMonthIcon
                      sx={{
                        mr: 1,
                        color:
                          "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            {/* BUTTONS */}

            <Grid
              item
              xs={12}
              sm={6}
              md={6}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1}
                sx={{
                  height: "100%",
                  justifyContent:
                    "center",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={
                    handleApplyFilters
                  }
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    minWidth: 130,
                  }}
                >
                  Search
                </Button>

                <Button
                  variant="outlined"
                  onClick={
                    handleClearFilters
                  }
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Clear Filters
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ======================================================
          SUMMARY
      ====================================================== */}

      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Registers"
            value={registers.length}
            subtitle={`Total ${total}`}
            icon={
              <LocalShippingIcon />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Total Devices"
            value={totalItems}
            subtitle="Dispatched devices"
            icon={
              <Inventory2Icon />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="In Transit"
            value={totalPendingItems}
            subtitle="Device in transit"
            icon={
              <ScheduleIcon />
            }
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          md={3}
        >
          <SummaryCard
            title="Received"
            value={totalAcceptedItems}
            // subtitle={`Rejected ${totalRejectedItems}`}
            subtitle="Device received"
            icon={
              <CheckCircleIcon />
            }
          />
        </Grid>
      </Grid>

      {/* ======================================================
          LOADING
      ====================================================== */}

      {loading &&
        !registers.length && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        )}

      {/* ======================================================
          EMPTY STATE
      ====================================================== */}

      {!loading &&
        registers.length === 0 && (
          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              border:
                "1px solid #e2e8f0",
            }}
          >
            <LocalShippingIcon
              sx={{
                fontSize: 56,
                color:
                  "text.disabled",
                mb: 1,
              }}
            />

            <Typography
              variant="h6"
              fontWeight={700}
            >
              No outbound transfers
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              No dispatch registers were
              found for the selected filters.
            </Typography>
          </Paper>
        )}

      {/* ======================================================
          OUTBOUND REGISTERS
      ====================================================== */}

      <Stack spacing={2}>
        {registers.map(
          (register) => {
            const isExpanded =
              expandedRegister ===
              register.register_id;

            const items =
              Array.isArray(
                register.items
              )
                ? register.items
                : [];

            return (
              <Card
                key={
                  register.register_id
                }
                sx={{
                  borderRadius: 3,
                  border:
                    "1px solid #e2e8f0",
                  boxShadow:
                    "0 3px 15px rgba(15,23,42,0.04)",
                  overflow: "hidden",
                }}
              >
                {/* REGISTER HEADER */}

                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    cursor: "pointer",
                    "&:hover": {
                      background:
                        "#f8fafc",
                    },
                  }}
                  onClick={() =>
                    toggleRegister(
                      register.register_id
                    )
                  }
                >
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      md: "center",
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography
                          fontWeight={800}
                          color="#0f172a"
                        >
                          Register #
                          {
                            register.register_id
                          }
                        </Typography>

                        <Chip
                          label={getDirectionLabel(
                            register.direction
                          )}
                          size="small"
                          color={getDirectionColor(
                            register.direction
                          )}
                          variant="outlined"
                        />
                      </Stack>

                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={{
                          xs: 0.5,
                          sm: 2,
                        }}
                        sx={{
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          From:{" "}
                          <strong>
                            {register.from
                              ?.name ||
                              "-"}
                          </strong>
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          To:{" "}
                          <strong>
                            {register.to
                              ?.name ||
                              "-"}
                          </strong>
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Date:{" "}
                          <strong>
                            {formatDate(
                              register.dispatch_date
                            )}
                          </strong>
                        </Typography>
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent="flex-end"
                    >
                      <Chip
                        label={`${register.pending_count || 0} In Transit`}
                        color="warning"
                        size="small"
                      />

                      <Chip
                        label={`${register.accepted_count || 0} Received`}
                        color="success"
                        size="small"
                      />

                      {/* <Chip
                        label={`${register.rejected_count || 0} Rejected`}
                        color="error"
                        size="small"
                      /> */}

                      <IconButton
                        sx={{
                          transform:
                            isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          transition:
                            "transform 0.2s",
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>

                {/* DETAILS */}

                <Collapse
                  in={isExpanded}
                  timeout="auto"
                  unmountOnExit
                >
                  <Divider />

                  <CardContent>
                    {/* SHIPPING INFORMATION */}

                    <Grid
                      container
                      spacing={2}
                      sx={{ mb: 2 }}
                    >
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Tracking No."
                          value={
                            register.tracking_no ||
                            "-"
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Dispatch Mode"
                          value={
                            register.dispatch_mode ||
                            "-"
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Dispatch Date"
                          value={formatDate(
                            register.dispatch_date
                          )}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Total Items"
                          value={
                            register.item_count ||
                            items.length
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="In Transit"
                          value={
                            register.pending_count ||
                            0
                          }
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Received"
                          value={
                            register.accepted_count ||
                            0
                          }
                        />
                      </Grid>

                      {/* <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                      >
                        <DetailField
                          label="Rejected"
                          value={
                            register.rejected_count ||
                            0
                          }
                        />
                      </Grid> */}

                      {register.comments && (
                        <Grid
                          item
                          xs={12}
                        >
                          <DetailField
                            label="Comments"
                            value={
                              register.comments
                            }
                          />
                        </Grid>
                      )}
                    </Grid>

                    <Divider
                      sx={{ mb: 2 }}
                    />

                    {/* ITEM LIST */}

                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{ mb: 1.5 }}
                    >
                      Dispatched Devices
                    </Typography>

                    <Stack spacing={1.5}>
                      {items.length === 0 && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            textAlign:
                              "center",
                          }}
                        >
                          <Typography
                            color="text.secondary"
                          >
                            No item details
                            available.
                          </Typography>
                        </Paper>
                      )}

                      {items.map(
                        (item) => (
                          <Paper
                            key={
                              item.item_id
                            }
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                            }}
                          >
                            <Stack
                              direction={{
                                xs: "column",
                                md: "row",
                              }}
                              spacing={2}
                              justifyContent="space-between"
                              alignItems={{
                                xs: "flex-start",
                                md: "center",
                              }}
                            >
                              {/* DEVICE */}

                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                >
                                  <Typography
                                    fontWeight={800}
                                  >
                                    Item #
                                    {
                                      item.item_id
                                    }
                                  </Typography>

                                  {renderStatusChip(
                                    item.status
                                  )}
                                </Stack>

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    wordBreak:
                                      "break-all",
                                  }}
                                >
                                  IMEI:{" "}
                                  <strong>
                                    {item.imei ||
                                      "-"}
                                  </strong>
                                </Typography>
                              </Box>

                              {/* RECEIVED INFORMATION */}

                              <Stack
                                direction={{
                                  xs: "column",
                                  sm: "row",
                                }}
                                spacing={2}
                                sx={{
                                  minWidth: {
                                    xs: "100%",
                                    md: 420,
                                  },
                                }}
                              >
                                <DetailField
                                  label="Received At"
                                  value={formatDateTime(
                                    item.received_at
                                  )}
                                />

                                <DetailField
                                  label="Received By"
                                  value={
                                    typeof item.received_by ===
                                    "object"
                                      ? item
                                          .received_by
                                          ?.name ||
                                        item
                                          .received_by
                                          ?.email ||
                                        "-"
                                      : item.received_by ||
                                        "-"
                                  }
                                />
                              </Stack>
                            </Stack>

                            {/* REJECTION REASON */}

                            {/* {item.reject_reason && (
                              <Box
                                sx={{
                                  mt: 1.5,
                                  p: 1.5,
                                  borderRadius: 2,
                                  background:
                                    "#fef2f2",
                                  border:
                                    "1px solid #fecaca",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="error"
                                  fontWeight={700}
                                >
                                  Reject Reason
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color="error"
                                  sx={{ mt: 0.25 }}
                                >
                                  {
                                    item.reject_reason
                                  }
                                </Typography>
                              </Box>
                            )} */}
                          </Paper>
                        )
                      )}
                    </Stack>

                    {/* DETAIL BUTTON */}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "flex-end",
                        mt: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();

                          handleOpenDetail(
                            register.register_id
                          );
                        }}
                        disabled={
                          detailLoading
                        }
                        sx={{
                          textTransform:
                            "none",
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        View Full Details
                      </Button>
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            );
          }
        )}
      </Stack>

      {/* ======================================================
          PAGINATION
      ====================================================== */}

      {total > PAGE_SIZE && (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            disabled={
              page <= 1 ||
              loading
            }
            onClick={() =>
              setPage(
                (prev) =>
                  Math.max(
                    1,
                    prev - 1
                  )
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Previous
          </Button>

          <Chip
            label={`Page ${page}`}
          />

          <Button
            variant="outlined"
            disabled={
              page * PAGE_SIZE >=
                total ||
              loading
            }
            onClick={() =>
              setPage(
                (prev) =>
                  prev + 1
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Next
          </Button>
        </Stack>
      )}

      {/* ======================================================
          FULL DETAIL DIALOG
      ====================================================== */}

      <Dialog
        open={
          detailDialog.open
        }
        onClose={
          handleCloseDetail
        }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          Transfer Register Details

          <IconButton
            onClick={
              handleCloseDetail
            }
            sx={{
              position:
                "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detailLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "center",
                py: 5,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {detailDialog.register && (
                <>
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 3 }}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                    >
                      <DetailField
                        label="Register ID"
                        value={
                          detailDialog
                            .register
                            ?.register_id ||
                          detailDialog
                            .register
                            ?.register
                            ?.id ||
                          "-"
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                    >
                      <DetailField
                        label="Direction"
                        value={getDirectionLabel(
                          detailDialog
                            .register
                            ?.direction ||
                            detailDialog
                              .register
                              ?.register
                              ?.direction
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                    >
                      <DetailField
                        label="Tracking No."
                        value={
                          detailDialog
                            .register
                            ?.tracking_no ||
                          detailDialog
                            .register
                            ?.register
                            ?.tracking_no ||
                          "-"
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                    >
                      <DetailField
                        label="Dispatch Date"
                        value={formatDate(
                          detailDialog
                            .register
                            ?.dispatch_date ||
                            detailDialog
                              .register
                              ?.register
                              ?.dispatch_date
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ mb: 1.5 }}
                  >
                    Items
                  </Typography>

                  <Stack spacing={1.5}>
                    {(
                      detailDialog
                        .register
                        ?.items || []
                    ).map(
                      (item) => (
                        <Paper
                          key={
                            item.item_id
                          }
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                          }}
                        >
                          <Stack
                            direction={{
                              xs: "column",
                              md: "row",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography
                                fontWeight={800}
                              >
                                Item #
                                {
                                  item.item_id
                                }
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                }}
                              >
                                IMEI:{" "}
                                <strong>
                                  {item.imei ||
                                    "-"}
                                </strong>
                              </Typography>
                            </Box>

                            {renderStatusChip(
                              item.status
                            )}

                            <DetailField
                              label="Received At"
                              value={formatDateTime(
                                item.received_at
                              )}
                            />

                            <DetailField
                              label="Received By"
                              value={
                                typeof item.received_by ===
                                "object"
                                  ? item
                                      .received_by
                                      ?.name ||
                                    item
                                      .received_by
                                      ?.email ||
                                    "-"
                                  : item.received_by ||
                                    "-"
                              }
                            />
                          </Stack>

                          {item.reject_reason && (
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{
                                mt: 1.5,
                              }}
                            >
                              <strong>
                                Reject Reason:
                              </strong>{" "}
                              {
                                item.reject_reason
                              }
                            </Typography>
                          )}
                        </Paper>
                      )
                    )}
                  </Stack>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              handleCloseDetail
            }
            sx={{
              textTransform:
                "none",
              fontWeight: 700,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={
          closeSnackbar
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
          onClose={
            closeSnackbar
          }
          sx={{
            width: "100%",
          }}
        >
          {
            snackbar.message
          }
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * ============================================================
 * SUMMARY CARD
 * ============================================================
 */

const SummaryCard = ({
  title,
  value,
  subtitle,
  icon,
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border:
          "1px solid #e2e8f0",
        boxShadow:
          "0 3px 15px rgba(15,23,42,0.04)",
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 0.5 }}
            >
              {value}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          </Box>

          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "#eff6ff",
              color:
                "#2563eb",
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

/**
 * ============================================================
 * DETAIL FIELD
 * ============================================================
 */

const DetailField = ({
  label,
  value,
}) => {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={700}
        sx={{
          mt: 0.25,
          wordBreak:
            "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default DispatchOutbound;